import vacationV2Service from "@/services/vacationV2Service";
import vacationV2VoicemailService from "@/services/vacationV2VoicemailService";

const state = {
	vacations: [],
	vacationVoicemails: [],
	currentVacation: null,
	loading: false,
	error: null
};

const getters = {
	getAllVacations: state => state.vacations,
	getVacationById: state => id => state.vacations.find(v => v.id === id),
	getCurrentVacation: state => state.currentVacation,
	isVacationLoading: state => state.loading,
	vacationError: state => state.error,

	getAllVacationVoicemails: state => state.vacationVoicemails,
	getVoicemailById: state => id => state.vacationVoicemails.find(vm => vm.id === id)
};

const mutations = {
	setVacations(state, vacations) {
		state.vacations = vacations ?? [];
	},
	addVacation(state, vacation) {
		state.vacations.push(vacation);
	},
	updateVacation(state, updated) {
		const index = state.vacations.findIndex(v => v.id === updated.id);

		if (index !== -1) {
			state.vacations.splice(index, 1, updated);
		}
	},
	removeVacation(state, id) {
		state.vacations = state.vacations.filter(v => v.id !== id);
	},
	setCurrentVacation(state, vacation) {
		state.currentVacation = vacation;
	},
	setLoading(state, value) {
		state.loading = value;
	},
	setError(state, error) {
		state.error = error;
	},
	clearError(state) {
		state.error = null;
	},

	setVacationVoicemails(state, voicemails) {
		state.vacationVoicemails = voicemails ?? [];
	},
	addVacationVoicemail(state, voicemail) {
		const index = state.vacationVoicemails.findIndex(vm => vm.id === voicemail.id);

		if (index !== -1) {
			state.vacationVoicemails.splice(index, 1, voicemail); // Update
		} else {
			state.vacationVoicemails.push(voicemail); // Add
		}
	},
	updateVacationVoicemail(state, updated) {
		const index = state.vacationVoicemails.findIndex(vm => vm.id === updated.id);

		if (index !== -1) {
			state.vacationVoicemails.splice(index, 1, updated);
		} else {
			state.vacationVoicemails.push(updated); // for a new one
		}
	},
	removeVacationVoicemail(state, id) {
		state.vacationVoicemails = state.vacationVoicemails.filter(vm => vm.id !== id);
	}
};

const actions = {
	async fetchVacations({ commit }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const vacations = await vacationV2Service.getAll();

			commit("setVacations", vacations);
			return vacations;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async fetchActiveVacations({ commit }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const vacations = await vacationV2Service.getActive();

			commit("setVacations", vacations);
			return vacations;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async fetchCurrentVacation({ commit }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const vacation = await vacationV2Service.getCurrent();

			commit("setCurrentVacation", vacation);
			return vacation;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async createVacation({ commit }, payload) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const newVacation = await vacationV2Service.create(payload);

			commit("addVacation", newVacation);
			return newVacation;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async bulkCreateVacations({ commit }, bulkPayload) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const newVacations = await vacationV2Service.bulkCreate(bulkPayload);

			commit("setVacations", newVacations);
			return newVacations;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async updateVacation({ commit }, { vacationId, payload }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const updated = await vacationV2Service.update(vacationId, payload);

			commit("updateVacation", updated);
			return updated;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async bulkUpdateVacations({ commit }, bulkPayload) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const updatedVacations = await vacationV2Service.bulkUpdate(bulkPayload);

			commit("setVacations", updatedVacations);
			return updatedVacations;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async removeVacation({ commit }, vacationId) {
		commit("setLoading", true);
		commit("clearError");
		try {
			await vacationV2Service.remove(vacationId);
			commit("removeVacation", vacationId);
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	clearError({ commit }) {
		commit("clearError");
	},

	clearCurrentVacation({ commit }) {
		commit("setCurrentVacation", null);
	},

	async fetchVacationVoicemails({ commit }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const voicemails = await vacationV2VoicemailService.getAll();

			commit("setVacationVoicemails", voicemails);
			return voicemails;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async createVacationVoicemail({ commit }, payload) {
		commit("setLoading", true);
		commit("clearError");
		try {
			const voicemail = await vacationV2VoicemailService.createGenerated(payload);

			commit("addVacationVoicemail", voicemail);
			return voicemail;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async uploadVacationVoicemailFile({ commit }, { file, disabled = false }) {
		commit("setLoading", true);
		commit("clearError");

		try {
			const updatedVoicemail = await vacationV2VoicemailService.uploadFile(file, disabled);

			// Update voicemail in array
			commit("updateVacationVoicemail", updatedVoicemail);

			return updatedVoicemail;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	async removeVacationVoicemail({ commit }, { id, inStoreOnly = false }) {
		commit("setLoading", true);
		commit("clearError");
		try {
			if (!inStoreOnly) {
				await vacationV2VoicemailService.remove(id);
			}
			commit("removeVacationVoicemail", id);
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
	init({ watch, login, dispatch }) {
		watch(
			() => login.profile?.id,
			(newId, oldId) => {
				if (!newId || newId === oldId) return;
				dispatch("fetchCurrentVacation");
			}
		);

		watch(
			() => login.refreshed,
			(refreshed) => {
				if (refreshed) {
					dispatch("fetchCurrentVacation");
				}
			}
		);
	}
};
